import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Filter,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Vote,
  Users,
  AlertCircle
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Votacoes = () => {
  const navigate = useNavigate();
  const { getCondominioId, isAdministrador } = useCondominio();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [showVotarModal, setShowVotarModal] = useState(false);
  const [showResultadosModal, setShowResultadosModal] = useState(false);
  const [votacaoSelecionada, setVotacaoSelecionada] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: ''
  });
  const [votoSelecionado, setVotoSelecionado] = useState(null);

  const podeCriarEditar = ['administrador', 'sindico', 'subsindico'].includes(user?.usuario_tipo);

  // Buscar votações
  const { data: votacoesData, isLoading } = useQuery(
    ['votacoes', getCondominioId(), searchTerm, filters],
    () => api.get('/votacoes', {
      params: {
        search: searchTerm,
        status: filters.status,
        condominio_id: getCondominioId()
      }
    }).then(res => res.data.data.votacoes || []),
    {
      enabled: !!getCondominioId()
    }
  );

  const votacoes = Array.isArray(votacoesData) ? votacoesData : [];

  // Registrar voto
  const votarMutation = useMutation(
    (data) => api.post(`/votacoes/${votacaoSelecionada.votacao_id}/votar`, data, {
      params: { condominio_id: getCondominioId() }
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['votacoes']);
        setShowVotarModal(false);
        setVotacaoSelecionada(null);
        toast.success('Voto registrado com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao registrar voto');
      }
    }
  );

  // Buscar resultados
  const { data: resultadosData, isLoading: isLoadingResultados } = useQuery(
    ['votacoes-resultados', votacaoSelecionada?.votacao_id],
    () => api.get(`/votacoes/${votacaoSelecionada.votacao_id}/resultados`, {
      params: { condominio_id: getCondominioId() }
    }).then(res => res.data.data),
    {
      enabled: !!votacaoSelecionada && showResultadosModal
    }
  );


  const handleDelete = async (votacao) => {
    if (!window.confirm('Tem certeza que deseja excluir esta votação?')) return;

    try {
      await api.delete(`/votacoes/${votacao.votacao_id}`, {
        params: { condominio_id: getCondominioId() }
      });
      queryClient.invalidateQueries(['votacoes']);
      toast.success('Votação excluída com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao excluir votação');
    }
  };


  const handleVotar = () => {
    if (!votoSelecionado) {
      toast.error('Selecione uma opção para votar');
      return;
    }

    votarMutation.mutate({
      votacao_item_id: votoSelecionado
    });
  };

  const handleAbrirVotar = async (votacao) => {
    setVotacaoSelecionada(votacao);
    
    // Verificar elegibilidade
    try {
      const response = await api.get(`/votacoes/${votacao.votacao_id}/elegibilidade`, {
        params: { condominio_id: getCondominioId() }
      });
      
      if (response.data.data.elegivel) {
        setShowVotarModal(true);
      } else {
        toast.error(response.data.data.motivo || 'Você não está elegível para votar');
      }
    } catch (error) {
      toast.error('Erro ao verificar elegibilidade');
    }
  };

  const handleVerResultados = (votacao) => {
    setVotacaoSelecionada(votacao);
    setShowResultadosModal(true);
  };


  const getStatusBadge = (status) => {
    const statusMap = {
      pendente: { color: 'bg-yellow-100 text-yellow-800', text: 'Pendente' },
      ativa: { color: 'bg-green-100 text-green-800', text: 'Ativa' },
      encerrada: { color: 'bg-gray-100 text-gray-800', text: 'Encerrada' },
      cancelada: { color: 'bg-red-100 text-red-800', text: 'Cancelada' }
    };
    return statusMap[status] || statusMap.pendente;
  };

  const getTipoValidacaoText = (tipo) => {
    const tipos = {
      em_dia: 'Em dia com boletos',
      procuração: 'Com procuração',
      liberacao: 'Com liberação',
      todos: 'Todos os usuários'
    };
    return tipos[tipo] || tipo;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Vote className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Votações</h1>
        </div>
        {podeCriarEditar && (
          <button
            onClick={() => navigate('/votacoes/nova')}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Votação
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por título ou descrição..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="ativa">Ativa</option>
              <option value="encerrada">Encerrada</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
        </div>
        </div>
      </div>

      {/* Lista de Votações */}
      <div className="grid gap-4">
        {votacoes.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <Vote size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Nenhuma votação encontrada</p>
          </div>
        ) : (
          votacoes.map((votacao) => {
            const statusBadge = getStatusBadge(votacao.votacao_status);
            const agora = new Date();
            const dataInicio = votacao.votacao_data_inicio ? new Date(votacao.votacao_data_inicio) : null;
            const dataFim = votacao.votacao_data_fim ? new Date(votacao.votacao_data_fim) : null;
            const estaAtiva = votacao.votacao_status === 'ativa' && 
              (!dataInicio || dataInicio <= agora) && 
              (!dataFim || dataFim >= agora);

            return (
              <div key={votacao.votacao_id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-800">{votacao.votacao_titulo}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge.color}`}>
                        {statusBadge.text}
                      </span>
                    </div>
                    {votacao.votacao_descricao && (
                      <p className="text-gray-600 mb-3">{votacao.votacao_descricao}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      {dataInicio && (
                        <div className="flex items-center gap-1">
                          <Calendar size={16} />
                          <span>Início: {new Date(dataInicio).toLocaleString('pt-BR')}</span>
                        </div>
                      )}
                      {dataFim && (
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span>Fim: {new Date(dataFim).toLocaleString('pt-BR')}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Users size={16} />
                        <span>Validação: {getTipoValidacaoText(votacao.votacao_tipo_validacao)}</span>
                      </div>
                      {votacao.itens && votacao.itens.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Vote size={16} />
                          <span>{votacao.itens.length} opções</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {estaAtiva && votacao.elegivel && (
                      <button
                        onClick={() => handleAbrirVotar(votacao)}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                      >
                        <Vote size={18} />
                        Votar
                      </button>
                    )}
                    {!votacao.elegivel && estaAtiva && (
                      <div className="flex items-center gap-2 text-orange-600 px-4 py-2">
                        <AlertCircle size={18} />
                        <span className="text-sm">{votacao.motivoElegibilidade}</span>
                      </div>
                    )}
                    <button
                      onClick={() => handleVerResultados(votacao)}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      <Eye size={18} />
                      Resultados
                    </button>
                    {podeCriarEditar && (
                      <>
                        <button
                          onClick={() => navigate(`/votacoes/${votacao.votacao_id}/editar`)}
                          className="flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(votacao)}
                          className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal Votar */}
      {showVotarModal && votacaoSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Votar: {votacaoSelecionada.votacao_titulo}</h2>
              {votacaoSelecionada.votacao_descricao && (
                <p className="text-gray-600 mb-6">{votacaoSelecionada.votacao_descricao}</p>
              )}
              
              <div className="space-y-3 mb-6">
                {votacaoSelecionada.itens?.map((item) => (
                  <label
                    key={item.votacao_item_id}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                      votoSelecionado === item.votacao_item_id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="voto"
                      value={item.votacao_item_id}
                      checked={votoSelecionado === item.votacao_item_id}
                      onChange={(e) => setVotoSelecionado(parseInt(e.target.value))}
                      className="mr-3"
                    />
                    <span className="text-lg">{item.votacao_item_texto}</span>
                  </label>
                ))}
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setShowVotarModal(false);
                    setVotacaoSelecionada(null);
                    setVotoSelecionado(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleVotar}
                  disabled={!votoSelecionado || votarMutation.isLoading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {votarMutation.isLoading ? 'Registrando...' : 'Confirmar Voto'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Resultados */}
      {showResultadosModal && votacaoSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Resultados: {votacaoSelecionada.votacao_titulo}</h2>
              
              {isLoadingResultados ? (
                <LoadingSpinner />
              ) : resultadosData ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-lg font-semibold text-blue-800">
                      Total de Votos: {resultadosData.votacao.totalVotos}
                    </p>
                  </div>
                  
                  {resultadosData.votacao.resultados.map((resultado) => {
                    const percentual = resultadosData.votacao.totalVotos > 0
                      ? ((resultado.totalVotos / resultadosData.votacao.totalVotos) * 100).toFixed(1)
                      : 0;
                    
                    return (
                      <div key={resultado.item_id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-semibold">{resultado.texto}</h3>
                          <span className="text-lg font-bold text-blue-600">
                            {resultado.totalVotos} votos ({percentual}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4 mb-3">
                          <div
                            className="bg-blue-600 h-4 rounded-full transition-all"
                            style={{ width: `${percentual}%` }}
                          />
                        </div>
                        {resultado.votos.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-2">Votantes:</p>
                            <div className="space-y-1">
                              {resultado.votos.map((voto, index) => (
                                <div key={index} className="text-sm text-gray-600">
                                  {voto.usuario_nome}
                                  {voto.apartamento && ` (${voto.apartamento})`}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-600">Nenhum resultado disponível</p>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowResultadosModal(false);
                    setVotacaoSelecionada(null);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Votacoes;

